// Procedural settlements around each city centre — the houses, townhouses,
// keeps and ruins that make a city read as a place instead of a single
// voxel prefab. Same approach as TerrainScatter's trees:
// - deterministic from the city's hex (q, r): a city looks the same on every
//   load, and houses don't reshuffle when the snapshot ticks;
// - each city leans toward one building culture (timber / stone / brick) so
//   it reads as coherent, with a minority of other styles mixed in;
// - size grows with tier (village → town → capital with a keep roofed in the
//   owner's colour);
// - wear: a damaged city (hp below max) shows roofless ruins, and every
//   building carries a small weathering tint jitter.
//
// Split across three modules: settlementForms.ts (geometry + palettes),
// settlementBuild.ts (deterministic layout builders) and SettlementPieces.tsx
// (the instanced rendering pieces). This file is the public entry + the
// top-level component.

import type { SceneNode } from '@aod/protocol';
import { useMemo } from 'react';
import {
  ChimneySmoke,
  FormBatch,
  WindowLights,
  chimneyPoints,
  windowLights,
} from './SettlementPieces.js';
import { reliefAt, tileSurfaceY } from './elevation.js';
import { type SettlementBuilding, buildSettlement } from './settlementBuild.js';
import type { BuildingForm } from './settlementForms.js';

export { buildSettlement, buildPathProps, buildFortifications } from './settlementBuild.js';
export { chimneyPoints, windowLights, type WindowLight } from './SettlementPieces.js';

interface SettlementsProps {
  readonly cities: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
  readonly onClick?: (city: SceneNode) => void;
}

export function Settlements({ cities, biomeMap, onClick }: SettlementsProps) {
  // Key the layout on what changes it (position, tier, damage, owner), not on
  // the snapshot object — cities are new objects every tick.
  const layoutKey = cities
    .map((c) => {
      const p = (c.props ?? {}) as Record<string, unknown>;
      const built = ((p.completedBuildings as string[] | undefined) ?? [])
        .filter((b) => b.startsWith('walls_') || b.startsWith('tower_'))
        .join('+');
      return `${c.id}:${p.q},${p.r}:${p.tier}:${p.hp}/${p.hpMax}:${p.ownerId}:${p.isCapital}:${built}:${p.archetype ?? ''}:${p.alignment ?? ''}:${p.culture ?? ''}`;
    })
    .join('|');

  const byForm = useMemo(() => {
    const groups = new Map<BuildingForm, SettlementBuilding[]>();
    for (const city of cities) {
      const p = (city.props ?? {}) as Record<string, unknown>;
      const q = (p.q as number) ?? 0;
      const r = (p.r as number) ?? 0;
      const biome = biomeMap.get(`${q},${r}`) ?? 'plains';
      const [cx, , cz] = city.transform.position;
      for (const raw of buildSettlement(city, tileSurfaceY(q, r, biomeMap))) {
        // Follow the tile's relief so houses on a hill aren't buried in it.
        const b = { ...raw, y: raw.y + reliefAt(biome, raw.x - cx, raw.z - cz) };
        const list = groups.get(b.form);
        if (list) list.push(b);
        else groups.set(b.form, [b]);
      }
    }
    return groups;
  }, [layoutKey, biomeMap]);

  const handleClick = (cityId: string) => {
    const node = cities.find((c) => c.id === cityId);
    if (node) onClick?.(node);
  };

  const chimneys = useMemo(() => chimneyPoints([...byForm.values()].flat()), [byForm]);
  const windows = useMemo(() => windowLights([...byForm.values()].flat()), [byForm]);

  return (
    <>
      {Array.from(byForm.entries()).map(([form, buildings]) => (
        <FormBatch key={form} form={form} buildings={buildings} onClick={handleClick} />
      ))}
      <ChimneySmoke points={chimneys} />
      <WindowLights windows={windows} />
    </>
  );
}
